import React, { useState, useEffect } from "react";
import { convertUnits, UNIT_OPTIONS } from "../../utils/calculations";

const UnitConversionCalculator = ({ onSave }) => {
  const [category, setCategory] = useState("length");

  const [params, setParams] = useState({
    value: 1,
    fromUnit: "mm",
    toUnit: "in",
  });

  const [results, setResults] = useState(null);

  useEffect(() => {
    const calcResults = convertUnits(params);
    setResults(calcResults);
  }, [params]);

  const handleInputChange = (field, value) => {
    setParams({
      ...params,
      [field]: field === "value" ? (value === "" ? "" : parseFloat(value)) : value,
    });
  };

  const handleCategoryChange = (newCategory) => {
    const firstUnit = UNIT_OPTIONS[newCategory][0].value;

    setCategory(newCategory);
    setParams({
      ...params,
      fromUnit: firstUnit,
      toUnit: firstUnit,
    });
  };

  const resetForm = () => {
    setCategory("length");
    setParams({
      value: 1,
      fromUnit: "mm",
      toUnit: "in",
    });
  };

  const copyToClipboard = () => {
    if (results) {
      const text = `${params.value} ${params.fromUnit} = ${results.result} ${params.toUnit}`;
      navigator.clipboard.writeText(text);
    }
  };

  const handleSave = () => {
    if (!results || !onSave) return;

    onSave({
      summary: `${params.value} ${params.fromUnit} → ${results.result} ${params.toUnit}`,
      input: {
        category,
        value: params.value,
        fromUnit: params.fromUnit,
      },
      results: {
        convertedValue: results.result,
        toUnit: params.toUnit,
      },
      warnings: Array.isArray(results.warnings) ? results.warnings : [],
      confidence: results.confidence || "High",
    });
  };

  const unitList = UNIT_OPTIONS[category];

  return (
    <div className="calculator-page">
      <div className="calculator-shell">
        <div className="calculator-header" style={{ marginBottom: "24px" }}>
          <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
            📏 Unit Conversion Calculator
          </h1>
          <p style={{ color: "var(--text-muted)", margin: 0 }}>
            Convert between different units of measurement
          </p>
        </div>

        <div className="calculator-layout calculator-layout-wide-results">
          <div className="calculator-panel">
            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                Category
              </label>
              <select
                value={category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                }}
              >
                {Object.keys(UNIT_OPTIONS).map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                Value
              </label>
              <input
                type="number"
                step="any"
                value={params.value}
                onChange={(e) => handleInputChange("value", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                From Unit
              </label>
              <select
                value={params.fromUnit}
                onChange={(e) => handleInputChange("fromUnit", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                }}
              >
                {unitList.map((unit) => (
                  <option key={unit.value} value={unit.value}>
                    {unit.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                To Unit
              </label>
              <select
                value={params.toUnit}
                onChange={(e) => handleInputChange("toUnit", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                }}
              >
                {unitList.map((unit) => (
                  <option key={unit.value} value={unit.value}>
                    {unit.label}
                  </option>
                ))}
              </select>
            </div>

            <div
              className="form-actions"
              style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
            >
              <button onClick={resetForm} className="btn-secondary" type="button">
                Reset
              </button>
            </div>
          </div>

          <div className="calculator-panel">
            {results && (
              <>
                <div
                  className="results-header"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginBottom: "20px",
                  }}
                >
                  <h3 style={{ color: "var(--text-primary)", margin: 0 }}>
                    Results
                  </h3>
                  <div
                    className="confidence-indicator"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    Confidence:{" "}
                    <span style={{ color: "var(--accent)", fontWeight: 700 }}>
                      {results.confidence || "High"}
                    </span>
                  </div>
                </div>

                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                    marginBottom: "20px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Converted Value
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {results.result} {params.toUnit}
                  </span>
                </div>

                {results.warnings?.length > 0 && (
                  <div
                    className="warnings"
                    style={{
                      background: "rgba(245, 158, 11, 0.12)",
                      border: "1px solid rgba(245, 158, 11, 0.35)",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                    }}
                  >
                    <h4
                      style={{
                        color: "var(--text-primary)",
                        marginBottom: "10px",
                      }}
                    >
                      ⚠️ Warnings
                    </h4>
                    <ul
                      style={{
                        paddingLeft: "20px",
                        color: "var(--text-secondary)",
                        marginBottom: 0,
                      }}
                    >
                      {results.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div
                  className="results-actions"
                  style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
                >
                  <button onClick={handleSave} className="btn-primary" type="button">
                    💾 Save Calculation
                  </button>
                  <button
                    onClick={copyToClipboard}
                    className="btn-secondary"
                    type="button"
                  >
                    📄 Copy
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitConversionCalculator;
